import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./HowToApply.css";
import Seo from "../components/Seo";

const applicationSteps = [
  {
    icon: "bi bi-ui-checks-grid",
    title: "Share Your Loan Requirement",
    description:
      "Select the loan product and provide basic information such as required amount, city, occupation and income profile.",
    details: [
      "Choose the required loan product",
      "Enter applicant and contact details",
      "Provide the approximate loan amount",
      "Share basic income information",
    ],
  },
  {
    icon: "bi bi-telephone-inbound",
    title: "Speak with Our Loan Team",
    description:
      "A MONAYRA representative reviews the enquiry and connects with you to understand the requirement in detail.",
    details: [
      "Discuss your financial requirement",
      "Review basic eligibility factors",
      "Understand suitable lender options",
      "Receive the initial document checklist",
    ],
  },
  {
    icon: "bi bi-folder-check",
    title: "Submit Required Documents",
    description:
      "Submit the KYC, income, banking, employment, business or property documents required for the selected loan.",
    details: [
      "Submit identity and address proof",
      "Provide income and bank statements",
      "Share employment or business documents",
      "Submit property or vehicle papers where applicable",
    ],
  },
  {
    icon: "bi bi-bank",
    title: "Lender Application",
    description:
      "The completed application is submitted to an appropriate lender for verification and assessment.",
    details: [
      "Application form submission",
      "Credit-profile assessment",
      "Income and obligation verification",
      "Legal or technical checks where required",
    ],
  },
  {
    icon: "bi bi-file-earmark-check",
    title: "Sanction and Offer Review",
    description:
      "If approved, the lender communicates the eligible loan amount, interest rate, tenure, charges and conditions.",
    details: [
      "Review the sanction letter",
      "Understand applicable conditions",
      "Confirm rate, tenure and charges",
      "Complete pending lender formalities",
    ],
  },
  {
    icon: "bi bi-cash-coin",
    title: "Disbursement",
    description:
      "After all lender conditions and documentation are completed, the approved loan is disbursed according to the lender process.",
    details: [
      "Complete final documentation",
      "Submit disbursement-related papers",
      "Meet applicable lender conditions",
      "Receive disbursement confirmation",
    ],
  },
];

const loanProducts = [
  {
    icon: "bi bi-house-door",
    title: "Home Loan",
    description:
      "For eligible home purchase, construction, extension or renovation requirements.",
    path: "/home-loan",
  },
  {
    icon: "bi bi-buildings",
    title: "Loan Against Property",
    description:
      "For eligible funding secured against residential or commercial property.",
    path: "/loan-against-property",
  },
  {
    icon: "bi bi-person-check",
    title: "Personal Loan",
    description:
      "For eligible personal requirements without property collateral.",
    path: "/personal-loan",
  },
  {
    icon: "bi bi-briefcase",
    title: "Business Loan",
    description:
      "For eligible business expansion, working capital and operational requirements.",
    path: "/business-loan",
  },
  {
    icon: "bi bi-car-front",
    title: "Car Loan",
    description:
      "For eligible new-car, used-car and vehicle-finance requirements.",
    path: "/car-loan",
  },
  {
    icon: "bi bi-mortarboard",
    title: "Education Loan",
    description:
      "For eligible higher education in India or overseas institutions.",
    path: "/education-loan",
  },
];

const preparationTips = [
  {
    icon: "bi bi-person-vcard",
    title: "Keep KYC Ready",
    description:
      "Keep PAN, Aadhaar, address proof and recent photographs available.",
  },
  {
    icon: "bi bi-bank",
    title: "Organise Bank Statements",
    description:
      "Keep recent personal, salary or business bank statements ready.",
  },
  {
    icon: "bi bi-file-earmark-bar-graph",
    title: "Prepare Income Proof",
    description:
      "Collect salary slips, Form 16, ITRs or business financial statements.",
  },
  {
    icon: "bi bi-credit-card",
    title: "Declare Existing Loans",
    description:
      "Share accurate information about current EMIs and financial obligations.",
  },
];

const processHighlights = [
  {
    icon: "bi bi-search",
    title: "Profile Review",
    description:
      "Your income, occupation, obligations and required loan amount are reviewed.",
  },
  {
    icon: "bi bi-bank",
    title: "Lender Comparison",
    description:
      "Potentially suitable lenders are compared based on the applicant profile.",
  },
  {
    icon: "bi bi-file-earmark-text",
    title: "Document Assistance",
    description:
      "Receive guidance on commonly required documents and pending requirements.",
  },
  {
    icon: "bi bi-graph-up-arrow",
    title: "Application Updates",
    description:
      "Receive assistance with lender coordination and application progress.",
  },
];

const faqItems = [
  {
    question: "Can I submit a loan enquiry online?",
    answer:
      "Yes. You can submit your basic requirement through the online application page. Our team may contact you for additional details.",
  },
  {
    question: "Which loan products can I apply for?",
    answer:
      "You may submit an enquiry for home loans, Loan Against Property, personal loans, business loans, car loans and education loans.",
  },
  {
    question: "Which documents will be required?",
    answer:
      "The document list depends on the loan type and applicant profile. Common documents include KYC, bank statements, income proof and employment or business proof.",
  },
  {
    question: "How long does loan processing take?",
    answer:
      "The processing timeline depends on document completeness, applicant verification, credit assessment, loan type and lender policy.",
  },
  {
    question: "Does submitting an enquiry guarantee approval?",
    answer:
      "No. Final approval, loan amount, interest rate, tenure and other terms are decided by the selected lender after complete assessment.",
  },
  {
    question: "Can MONAYRA help me compare lenders?",
    answer:
      "MONAYRA can assist with an initial profile review, selected lender comparison, documentation guidance and application coordination.",
  },
];

const HowToApply = () => {
  const [openStep, setOpenStep] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <Seo
        title="How to Apply for a Loan | MONAYRA"
        description="Learn how to apply for a home loan, Loan Against Property, personal loan, business loan, car loan or education loan with MONAYRA (OPC) PRIVATE LIMITED."
        keywords="how to apply for loan, loan application process, home loan application, personal loan process, business loan process, car loan application"
        canonical="https://www.mortbuzz.in/how-to-apply"
      />

      <main className="monayra-how-apply-page">
        <section className="how-apply-hero">
          <div className="how-apply-hero-overlay"></div>

          <div
            className="how-apply-glow how-apply-glow-left"
            aria-hidden="true"
          ></div>

          <div
            className="how-apply-glow how-apply-glow-right"
            aria-hidden="true"
          ></div>

          <div className="how-apply-container how-apply-hero-grid">
            <div className="how-apply-hero-content">
              <span className="how-apply-eyebrow">
                Loan Application Guide
              </span>

              <h1>
                Apply for a loan through a
                <span> clear step-by-step process</span>
              </h1>

              <p>
                From submitting your requirement to lender assessment and
                disbursement, understand how MONAYRA (OPC) PRIVATE LIMITED
                assists you throughout the loan application journey.
              </p>

              <div className="how-apply-hero-actions">
                <Link
                  to="/apply-now"
                  className="how-apply-primary-btn"
                >
                  Start Your Application
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <a
                  href="#application-process"
                  className="how-apply-secondary-btn"
                >
                  <i className="bi bi-list-ol"></i>
                  View Application Steps
                </a>
              </div>

              <div className="how-apply-hero-features">
                <div>
                  <i className="bi bi-ui-checks"></i>
                  Simple enquiry process
                </div>

                <div>
                  <i className="bi bi-file-earmark-check"></i>
                  Document guidance
                </div>

                <div>
                  <i className="bi bi-headset"></i>
                  Application assistance
                </div>
              </div>
            </div>

            <aside className="how-apply-hero-card">
              <span className="how-apply-hero-card-icon">
                <i className="bi bi-send-check"></i>
              </span>

              <span className="how-apply-card-eyebrow">
                Begin Online
              </span>

              <h2>Share your loan requirement</h2>

              <p>
                Submit your basic details and let our team understand your
                loan requirement and applicant profile.
              </p>

              <div className="how-apply-hero-card-list">
                <div>
                  <i className="bi bi-check-circle"></i>
                  Select your loan product
                </div>

                <div>
                  <i className="bi bi-check-circle"></i>
                  Enter applicant information
                </div>

                <div>
                  <i className="bi bi-check-circle"></i>
                  Share income and loan details
                </div>

                <div>
                  <i className="bi bi-check-circle"></i>
                  Receive a call from our team
                </div>
              </div>

              <Link
                to="/apply-now"
                className="how-apply-primary-btn"
              >
                Apply Online
                <i className="bi bi-arrow-right"></i>
              </Link>
            </aside>
          </div>
        </section>

        <section className="how-apply-highlights-section">
          <div className="how-apply-container">
            <div className="how-apply-section-header">
              <span className="how-apply-eyebrow">
                Application Assistance
              </span>

              <h2>
                Support throughout your
                <span> loan journey</span>
              </h2>

              <p>
                Get assistance with profile review, lender comparison,
                documents and application coordination.
              </p>
            </div>

            <div className="how-apply-highlights-grid">
              {processHighlights.map((item) => (
                <article
                  className="how-apply-highlight-card"
                  key={item.title}
                >
                  <span>
                    <i className={item.icon}></i>
                  </span>

                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="application-process"
          className="how-apply-process-section"
        >
          <div className="how-apply-container">
            <div className="how-apply-section-header">
              <span className="how-apply-eyebrow">
                Application Process
              </span>

              <h2>
                Six stages of a typical
                <span> loan application</span>
              </h2>

              <p>
                The exact process may vary based on the loan product,
                applicant profile and selected lender.
              </p>
            </div>

            <div className="how-apply-process-layout">
              <div className="how-apply-process-list">
                {applicationSteps.map((step, index) => {
                  const isOpen = openStep === index;

                  return (
                    <article
                      className={`how-apply-step-item ${
                        isOpen ? "open" : ""
                      }`}
                      key={step.title}
                    >
                      <button
                        type="button"
                        className="how-apply-step-trigger"
                        onClick={() =>
                          setOpenStep(isOpen ? null : index)
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="how-apply-step-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="how-apply-step-icon">
                          <i className={step.icon}></i>
                        </span>

                        <span className="how-apply-step-title">
                          <strong>{step.title}</strong>
                          <small>{step.description}</small>
                        </span>

                        <span className="how-apply-step-toggle">
                          <i
                            className={
                              isOpen
                                ? "bi bi-dash-lg"
                                : "bi bi-plus-lg"
                            }
                          ></i>
                        </span>
                      </button>

                      {isOpen && (
                        <div className="how-apply-step-content">
                          <div className="how-apply-step-details">
                            {step.details.map((detail) => (
                              <div key={detail}>
                                <i className="bi bi-check-circle"></i>
                                {detail}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <aside className="how-apply-process-summary">
                <span className="how-apply-process-summary-icon">
                  <i className="bi bi-clipboard2-check"></i>
                </span>

                <span className="how-apply-card-eyebrow">
                  Before You Apply
                </span>

                <h3>Keep your basic information ready</h3>

                <p>
                  Preparing accurate information can help reduce avoidable
                  delays during application assessment.
                </p>

                <ul>
                  <li>
                    <i className="bi bi-check-lg"></i>
                    Required loan amount
                  </li>

                  <li>
                    <i className="bi bi-check-lg"></i>
                    Monthly or annual income
                  </li>

                  <li>
                    <i className="bi bi-check-lg"></i>
                    Existing EMI obligations
                  </li>

                  <li>
                    <i className="bi bi-check-lg"></i>
                    Employment or business details
                  </li>

                  <li>
                    <i className="bi bi-check-lg"></i>
                    Property, vehicle or course details
                  </li>
                </ul>

                <Link
                  to="/eligibility"
                  className="how-apply-secondary-btn"
                >
                  <i className="bi bi-files"></i>
                  View Document Checklist
                </Link>

                <Link
                  to="/apply-now"
                  className="how-apply-primary-btn"
                >
                  Start Application
                  <i className="bi bi-arrow-right"></i>
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <section className="how-apply-products-section">
          <div className="how-apply-container">
            <div className="how-apply-section-header">
              <span className="how-apply-eyebrow">
                Available Loan Products
              </span>

              <h2>
                Select the loan that matches
                <span> your requirement</span>
              </h2>

              <p>
                Explore the general purpose and application information for
                each loan category.
              </p>
            </div>

            <div className="how-apply-products-grid">
              {loanProducts.map((product) => (
                <article
                  className="how-apply-product-card"
                  key={product.title}
                >
                  <span>
                    <i className={product.icon}></i>
                  </span>

                  <h3>{product.title}</h3>
                  <p>{product.description}</p>

                  <Link to={product.path}>
                    Explore Product
                    <i className="bi bi-arrow-right"></i>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="how-apply-preparation-section">
          <div className="how-apply-container how-apply-preparation-grid">
            <div className="how-apply-preparation-content">
              <span className="how-apply-eyebrow">
                Prepare Your Application
              </span>

              <h2>
                Organise important information
                <span> before applying</span>
              </h2>

              <p>
                Complete and accurate information can make the loan-review
                process clearer and help the lender assess your profile.
              </p>

              <div className="how-apply-preparation-list">
                {preparationTips.map((tip) => (
                  <article key={tip.title}>
                    <span>
                      <i className={tip.icon}></i>
                    </span>

                    <div>
                      <h3>{tip.title}</h3>
                      <p>{tip.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="how-apply-checklist-card">
              <span className="how-apply-checklist-icon">
                <i className="bi bi-list-check"></i>
              </span>

              <span className="how-apply-card-eyebrow">
                Quick Checklist
              </span>

              <h3>Information commonly requested</h3>

              <div className="how-apply-checklist-items">
                {[
                  "Applicant name and contact information",
                  "Occupation or business profile",
                  "Monthly income or business turnover",
                  "Required loan amount",
                  "Current EMIs and financial obligations",
                  "Property, vehicle or education details",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="how-apply-primary-btn"
              >
                Apply Online
                <i className="bi bi-arrow-right"></i>
              </Link>
            </aside>
          </div>
        </section>

        <section className="how-apply-faq-section">
          <div className="how-apply-container">
            <div className="how-apply-section-header">
              <span className="how-apply-eyebrow">
                Common Questions
              </span>

              <h2>
                Loan-application
                <span> frequently asked questions</span>
              </h2>
            </div>

            <div className="how-apply-faq-layout">
              <div className="how-apply-faq-list">
                {faqItems.map((faq, index) => {
                  const isOpen = openFaq === index;

                  return (
                    <article
                      className={`how-apply-faq-item ${
                        isOpen ? "open" : ""
                      }`}
                      key={faq.question}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenFaq(isOpen ? null : index)
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="how-apply-faq-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span>{faq.question}</span>

                        <i
                          className={
                            isOpen
                              ? "bi bi-dash-lg"
                              : "bi bi-plus-lg"
                          }
                        ></i>
                      </button>

                      {isOpen && (
                        <div className="how-apply-faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <aside className="how-apply-help-card">
                <span className="how-apply-help-icon">
                  <i className="bi bi-headset"></i>
                </span>

                <span className="how-apply-card-eyebrow">
                  Need Assistance?
                </span>

                <h3>Speak with our loan team</h3>

                <p>
                  Share your loan type, required amount, income profile and
                  city to understand suitable next steps.
                </p>

                <Link
                  to="/contact"
                  className="how-apply-primary-btn"
                >
                  Contact Our Team
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/emi-calculator"
                  className="how-apply-secondary-btn"
                >
                  <i className="bi bi-calculator"></i>
                  Calculate EMI
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <section className="how-apply-cta-section">
          <div className="how-apply-container">
            <div className="how-apply-cta-card">
              <div>
                <span>Ready to Begin?</span>

                <h2>Submit your loan requirement to MONAYRA</h2>

                <p>
                  Start your application and receive assistance with
                  eligibility, documents, lender comparison and application
                  coordination.
                </p>
              </div>

              <div className="how-apply-cta-actions">
                <Link
                  to="/apply-now"
                  className="how-apply-primary-btn"
                >
                  Apply Now
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/contact"
                  className="how-apply-secondary-btn"
                >
                  <i className="bi bi-telephone"></i>
                  Contact Our Team
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="how-apply-disclaimer">
          <div className="how-apply-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              The steps and document requirements shown are indicative.
              Actual processing stages, timelines, approval, interest rate,
              eligible amount, tenure, fees and disbursement conditions are
              determined by the selected lender. MONAYRA (OPC) PRIVATE
              LIMITED provides loan assistance and does not guarantee
              approval or disbursement.
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default HowToApply;